/* eslint-disable react/prop-types */
import * as React from 'react';
import { memo } from 'react';
import { FilterCondition } from '../../../common/classes/Filter/FilterCondition';
import { Column } from '../../../common/interfaces';
import { ReportColumnOptionsResponse } from '../../../common/interfaces/report/types';
import { AutomationIds } from '../../../common/enums/AutomationElements.enum';
import { LanguageElements } from '../../../language-elements/LanguageElements';
import FilterConditionBase from './FilterConditionBase';
import { getConditionFieldOptions, getConditionOperatorOptions, getFilterSelection, getConditionMultiSelectOptions } from './FilterWrapperUtils';
import { FilterConditionUI } from './types';

interface FilterConditionProps {
    condition: FilterConditionUI;
    fields: Column[];
    index: number;
    showErrors: boolean;
    disableDeleteCondition: boolean;
    activeCondition: number;
    languageElements: LanguageElements;
    onUpdateFilterCondition: (condition: FilterCondition, conditionIndex: number) => void;
    onDeleteFilterCondition: (conditionIndex: number) => void;
    updateActiveConditionIndex: (conditionIndex: number) => void;
    reportColumnOptions?: ReportColumnOptionsResponse | null;
}

// Memoized component for rendering individual filter conditions
const FilterConditionComponent: React.FC<FilterConditionProps> = memo(
    ({
        condition,
        fields,
        index,
        showErrors,
        disableDeleteCondition,
        activeCondition,
        languageElements,
        onUpdateFilterCondition,
        onDeleteFilterCondition,
        updateActiveConditionIndex,
        reportColumnOptions,
    }) => {
        const conditionFields = getConditionFieldOptions(condition.conditionFields ?? fields);
        const conditionOperators = getConditionOperatorOptions(condition.conditionOperators, languageElements);
        const conditionMultiSelectOptions = getConditionMultiSelectOptions(reportColumnOptions, condition.columnId);

        const selection = getFilterSelection(condition.value, conditionMultiSelectOptions, condition.operator);
        const filterCondition = new FilterCondition(fields, condition);

        return (
            <FilterConditionBase
                condition={filterCondition}
                fields={fields}
                onUpdateFilterCondition={onUpdateFilterCondition}
                onDeleteFilterCondition={onDeleteFilterCondition}
                data-client-id={AutomationIds.VIEW_FILTER_WRAPPER}
                index={index}
                showErrors={showErrors}
                disableDeleteCondition={disableDeleteCondition}
                conditionFieldOptions={conditionFields}
                conditionOperatorOptions={conditionOperators}
                conditionMultiSelectOptions={conditionMultiSelectOptions}
                selection={selection}
                isFocused={activeCondition === index}
                updateActiveConditionIndex={updateActiveConditionIndex}
            />
        );
    }
);

FilterConditionComponent.displayName = 'FilterCondition';

export default FilterConditionComponent;
